import { ArrowUp, Download01, InfoCircle, MessageChatCircle, XClose } from "@untitledui/icons";
import { useState } from "react";
import { useTypedText } from "@/pages/ask/hooks/useTypedText";
import { useAskSurface } from "@/pages/ask/lib/ask-surface";
import { cx } from "@/utils/cx";
import { UnboxMark } from "./UnboxMark";

export const DRAWER_PROMPTS = [
  "Which creators drove the most GMV in the last 30 days?",
  "Who received samples but hasn't posted yet?",
  "Which outreach sequence is getting the most replies?",
];

export function DrawerHeader({
  onClose,
  onSaveChat,
  canSaveChat,
}: {
  onClose?: () => void;
  onSaveChat: () => void;
  canSaveChat: boolean;
}) {
  const redesign = useAskSurface() === "drawer";
  return (
    <>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 z-20 inline-flex size-8 cursor-pointer items-center justify-center rounded-full text-white/60 transition-colors duration-150 hover:bg-white/10 hover:text-white"
        >
          <XClose width={18} height={18} />
        </button>
      )}

      <div className={`flex items-center justify-between gap-4 ${onClose ? "pr-10" : ""}`}>
        <div className="flex items-center gap-3">
          {redesign ? (
            <span className="inline-flex items-center justify-center">
              <UnboxMark size={24} className="text-[#c0a7f2]" />
            </span>
          ) : (
            <span className="inline-flex size-10 items-center justify-center rounded-full bg-white/10">
              <MessageChatCircle width={20} height={20} className="text-[#c0a7f2]" />
            </span>
          )}
          <h1 className="text-xl leading-[30px] font-normal text-white">Ask Unbox</h1>
        </div>

        {canSaveChat && (
          <button
            type="button"
            onClick={onSaveChat}
            className="inline-flex cursor-pointer items-center gap-1 rounded-md px-2.5 py-1.5 text-[13px] font-semibold text-white/70 transition-colors duration-100 hover:bg-white/10 hover:text-white"
          >
            <Download01 width={16} height={16} />
            Save chat
          </button>
        )}
      </div>
    </>
  );
}

type DrawerEmptyStateProps = {
  prompts?: string[];
  title?: string;
  onPickPrompt: (prompt: string) => void;
};

export function DrawerEmptyState(props: DrawerEmptyStateProps) {
  return useAskSurface() === "drawer" ? <TypedEmptyState {...props} /> : <PlainEmptyState {...props} />;
}

function PlainEmptyState({ prompts = DRAWER_PROMPTS, title, onPickPrompt }: DrawerEmptyStateProps) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <p className="text-base font-medium text-white">{title ?? "What do you want to know?"}</p>
        {!title && (
          <p className="text-sm text-brand-100">
            I can look across your creators, videos, products, outreach and brand safety — ask in your own words, or
            start with one of these.
          </p>
        )}
      </div>
      <div className="flex flex-col items-start gap-2">
        {prompts.map((prompt) => (
          <button
            key={prompt}
            type="button"
            onClick={() => onPickPrompt(prompt)}
            className="cursor-pointer rounded-full border border-white/20 bg-white/5 px-3.5 py-2 text-left text-sm text-white/85 transition-colors duration-150 hover:border-white/35 hover:bg-white/10 hover:text-white"
          >
            {prompt}
          </button>
        ))}
      </div>
    </div>
  );
}

function TypedEmptyState({ prompts = DRAWER_PROMPTS, title, onPickPrompt }: DrawerEmptyStateProps) {
  const heading = title ?? "What do you want to know?";
  const { typed, done } = useTypedText(heading);
  return (
    <div className="flex min-h-full flex-col justify-center gap-8 py-6">
      <div className="flex flex-col items-start gap-6">
        <span className="inline-flex size-14 items-center justify-center rounded-2xl bg-white/[0.07] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]">
          <UnboxMark size={30} motion="intro" className="text-[#d8c6ff]" />
        </span>
        {/* The hidden copy holds the final size, so the typing never reflows the prompts below. */}
        <h2 className="ask-greeting grid text-[34px] leading-[1.15]" aria-label={heading}>
          <span className="invisible col-start-1 row-start-1" aria-hidden="true">
            {heading}
          </span>
          <span className="col-start-1 row-start-1" aria-hidden="true">
            {typed}
            <span className="ask-caret" data-done={done} />
          </span>
        </h2>
      </div>
      <div className="flex flex-col items-start gap-2.5" inert={!done}>
        {prompts.map((prompt, i) => (
          <button
            key={prompt}
            type="button"
            onClick={() => onPickPrompt(prompt)}
            style={{ animationDelay: `${120 + i * 90}ms` }}
            className={cx(
              done ? "ask-rise" : "opacity-0",
              "cursor-pointer rounded-2xl border border-white/12 bg-white/[0.04] px-4 py-2.5 text-left text-sm text-white/85 transition-colors duration-200 hover:border-[#c9b2f5]/50 hover:bg-white/[0.09] hover:text-white",
            )}
          >
            {prompt}
          </button>
        ))}
      </div>
    </div>
  );
}

export function NoMemoryNotice() {
  if (useAskSurface() !== "drawer") {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-white/60">
        <InfoCircle width={14} height={14} className="mt-0.5 shrink-0" />
        <span>
          This is v1 — no memory included yet. Each question starts fresh, so I won't recall earlier turns in this chat.
        </span>
      </div>
    );
  }
  return (
    <p className="ask-memory-note">
      <span>Each question starts fresh — I don't remember earlier turns yet</span>
    </p>
  );
}

export function DrawerComposer({
  onSubmit,
  onStop,
  loading,
}: {
  onSubmit: (question: string) => void;
  onStop: () => void;
  loading: boolean;
}) {
  const [value, setValue] = useState("");
  const redesign = useAskSurface() === "drawer";

  const send = () => {
    const question = value.trim();
    if (!question || loading) return;
    onSubmit(question);
    setValue("");
  };

  return (
    <div
      className={
        redesign
          ? "flex h-14 shrink-0 items-center gap-3 rounded-2xl border border-white/12 bg-white/[0.06] pr-3 pl-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_12px_32px_-16px_rgba(10,4,24,0.7)] backdrop-blur-xl transition-shadow duration-200 focus-within:border-[#c0a7f2]/50 focus-within:shadow-[0_0_0_4px_rgba(192,167,242,0.14)]"
          : "flex h-14 shrink-0 items-center gap-3 rounded-xl border border-[#5e5e5e] bg-linear-to-r from-[rgba(128,123,132,0.3)] to-[rgba(216,173,253,0.3)] px-5 transition-shadow duration-200 focus-within:border-[#8c7a9e] focus-within:shadow-[0_0_0_4px_rgba(196,181,253,0.15)]"
      }
    >
      <input
        type="text"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") send();
        }}
        placeholder="Ask anything..."
        aria-label="Ask anything"
        className="flex-1 bg-transparent text-base text-[#fffaeb] outline-none placeholder:text-[#fffaeb]"
      />
      {loading ? (
        <button
          type="button"
          onClick={onStop}
          aria-label="Stop"
          className="inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-white text-gray-900 transition-transform duration-150 hover:scale-105 hover:bg-gray-100 active:scale-95"
        >
          <span className="size-2.5 rounded-[2px] bg-current" />
        </button>
      ) : (
        <button
          type="button"
          onClick={send}
          aria-label="Send"
          className="inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-white text-gray-900 transition-transform duration-150 hover:scale-105 hover:bg-gray-100 active:scale-95"
        >
          <ArrowUp width={16} height={16} />
        </button>
      )}
    </div>
  );
}
